<template>
  <div>
    <div class="word-card" v-if="similarity">
      <div class="word-title">{{ similarity.word }}发音解决方案二</div>

      <div class="info-panel syllabary-panel">
        <span class="panel-label">🔗 温馨提示</span>
        <span class="panel-text">✂️按照拼音的发音习惯进行发音。</span>
      </div>

      <!-- 独立音标 → 对应字母 -->
      <div class="info-panel letter-panel" v-if="individualLettersArr.length">
        <span class="panel-label">🎵独立音标 / 🔤对应字母</span>
        <div class="pair-list">
          <div v-for="(letter, i) in individualLettersArr" :key="i" class="pair-col">
            <span class="cell pho-cell">/{{ individualPhoneticsArr[i] }}/</span>
            <span class="cell letter-cell" @click="searchForSimilarity(i)">{{ letter }}</span>
          </div>
        </div>
      </div>

      <!-- 音素相似单词 -->
      <div class="info-panel result-panel" v-if="individualSimilarWordArr.length">
        <span class="panel-label">🎯 音素相似单词</span>
        <div class="similar-chips">
          <button
            v-for="(item, i) in individualSimilarWordArr"
            :key="i"
            class="similar-btn"
            @click="showWordDetail(i)"
          >
            <span v-for="(c, k) in item.chars" :key="k" class="btn-letter" :class="{ 'btn-letter-hl': c.highlight }">{{ c.ch }}</span>
          </button>
        </div>
      </div>

      <button class="back-btn" @click="$emit('back')">返回单词详解</button>
    </div>

    <div class="word-card empty-card" v-else>
      <span class="empty-text">{{ errorMessage || '暂无相似词信息' }}</span>
    </div>

    <!-- 浮动详情 -->
    <div class="float-tip" v-if="floatingData" @click="clearFloating">
      <div class="float-word">{{ floatingData.word }}</div>
      <div class="float-meaning">{{ floatingData.meaning }}</div>
      <div class="float-meta" v-if="floatingData.grade">
        <span class="float-meta-item">📚 年级：{{ floatingData.grade }}</span>
        <span class="float-meta-item">📖 学期：{{ floatingData.term }}</span>
        <span class="float-meta-item">📗 单元：{{ floatingData.unit }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../../utils/request'
import { depart, normalizeNewlines, markWordChars } from '../../utils/similar'

export default {
  name: 'ChineseSyllable',
  data() {
    return {
      similarity: null,
      errorMessage: '',
      individualLettersArr: [],
      individualPhoneticsArr: [],
      individualSimilarWordArr: [],
      floatingData: null
    }
  },
  computed: {
    word() {
      return this.$store.state.word
    }
  },
  watch: {
    word(val) {
      if (val && val.trim()) this.fetchSimilarity(val)
    }
  },
  mounted() {
    const word = (this.$store.state.word || '').trim()
    if (word) this.fetchSimilarity(word)
  },
  methods: {
    async fetchSimilarity(word) {
      if (this._fetchingWord === word) return
      this._fetchingWord = word
      try {
        const token = localStorage.getItem('satoken') || ''
        const res = await request.get('/newSimilarity/searchForSimilarity', {
          params: { word, code: token }
        })
        if (res.code === 200) {
          const info = res.NewSimilarity
          if (!info) {
            this.similarity = null
            this.errorMessage = '暂无单词数据'
            return
          }
          ['individualPhoneticsChinese', 'individualLettersChinese'].forEach((k) => {
            if (typeof info[k] === 'string') info[k] = normalizeNewlines(info[k])
          })

          this.similarity = info
          this.individualLettersArr = depart(info.individualLettersChinese)
          this.individualPhoneticsArr = depart(info.individualPhoneticsChinese)
          this.errorMessage = ''

          if (this.individualLettersArr.length > 0) {
            this.fetchSimilarWord(
              info.word,
              this.individualPhoneticsArr[0] || '',
              this.individualLettersArr[0] || ''
            )
          }
        }
      } catch (e) {
        this.errorMessage = '查询失败，请重新登录！'
        this.similarity = null
      } finally {
        this._fetchingWord = null
      }
    },

    searchForSimilarity(index) {
      this.clearFloating()
      this.fetchSimilarWord(
        this.$store.state.word,
        this.individualPhoneticsArr[index] || '',
        this.individualLettersArr[index] || ''
      )
    },

    async fetchSimilarWord(word, phone, letter) {
      if (!phone && !letter) return
      try {
        const res = await request.get('/newSimilarity/searchChineseSyllable', {
          params: { word: word || '', letter: letter || '', phone: phone || '' }
        })
        if (res.code === 200) {
          const list = res.ChineseSyllable || []
          const target = (letter || phone || '').replace(/·/g, '').toLowerCase()
          const finalList = list
            .map((item) => {
              const { chars, count } = markWordChars(item.word, target)
              return { ...item, chars, targetCount: count }
            })
            .filter((item) => item.targetCount <= 1)
            .map((item) => {
              const rest = { ...item }
              delete rest.targetCount
              return rest
            })
          this.individualSimilarWordArr = finalList
          if (!list.length) this.$message.info('未找到相似单词')
        }
      } catch (e) {
        // 拦截器已提示
      }
    },

    showWordDetail(index) {
      const item = this.individualSimilarWordArr[index]
      if (!item) return
      this.playWord(item.word)
      this.clearFloating()
      const fullMeaning = normalizeNewlines(item.meaning || '')
      const firstMeaning = fullMeaning ? fullMeaning.split('\n')[0].trim() : '暂无释义'
      this.floatingData = {
        word: item.word || '',
        meaning: firstMeaning || '暂无释义',
        grade: item.grade,
        term: item.term,
        unit: item.unit
      }
      this._floatingTimer = setTimeout(() => this.clearFloating(), 5000)
    },

    playWord(word) {
      if (!word) return
      const audio = new Audio(
        'https://dict.youdao.com/dictvoice?audio=' + encodeURIComponent(word) + '&type=1'
      )
      audio.play()
    },

    clearFloating() {
      if (this._floatingTimer) {
        clearTimeout(this._floatingTimer)
        this._floatingTimer = null
      }
      this.floatingData = null
    }
  }
}
</script>
